/**
 * Settings -> Support: the response times the desk promises.
 *
 * Eight numbers in one grid - four priorities, two clocks - because they are
 * read against each other, not one at a time. A form that asked for them on
 * eight separate lines would make an accidental inversion ("urgent gets a day,
 * low gets an hour") invisible until a ticket was already coloured wrong.
 *
 * Edited in HOURS and stored in minutes. Every promise an agency actually makes
 * is a round number of hours or days, and asking for 480 minutes invites an
 * arithmetic slip that nothing downstream can catch. Halves are allowed, so a
 * thirty-minute promise is still expressible.
 *
 * Nothing here is filled in by default. A target is not a suggestion in a
 * sidebar - it is a promise printed beside a real ticket - so an invented one
 * would turn rows red against a number nobody at this desk agreed to. The
 * suggested ladder is offered as placeholder text and by one button, and it is
 * visibly a suggestion until somebody accepts it.
 */
import { useEffect, useState } from "react";
import { Loader2, Save, Timer, Wand2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import {
  SLA_PRIORITIES,
  emptySlaTargets,
  formatSlaHours,
  getSlaTargets,
  parseSlaHours,
  putSlaTargets,
  type SlaPriority,
  type SlaTargets,
} from "../../../lib/sla";
import { Btn, Card, CardHead, listCard } from "../../ui";

/** Priority, first response, resolution. */
const COLS = "minmax(120px,1fr) minmax(120px,1fr) minmax(120px,1fr)";

/** What the form holds while it is being typed: raw strings, one per cell. */
type Draft = Record<SlaPriority, { first: string; resolve: string }>;

function toDraft(targets: SlaTargets): Draft {
  const out = {} as Draft;
  for (const p of SLA_PRIORITIES) {
    out[p] = {
      first: formatSlaHours(targets.firstResponseMins[p]),
      resolve: formatSlaHours(targets.resolveMins[p]),
    };
  }
  return out;
}

function fromDraft(draft: Draft): SlaTargets {
  const out = emptySlaTargets();
  for (const p of SLA_PRIORITIES) {
    out.firstResponseMins[p] = parseSlaHours(draft[p].first);
    out.resolveMins[p] = parseSlaHours(draft[p].resolve);
  }
  return out;
}

/** True for a cell that has something in it which is not a number. An empty
 *  cell is a real answer ("no promise") and is never an error. */
function isBad(raw: string): boolean {
  return raw.trim() !== "" && parseSlaHours(raw) === null;
}

function HourCell({
  value,
  placeholder,
  canEdit,
  onChange,
  label,
}: {
  value: string;
  placeholder: string;
  canEdit: boolean;
  onChange: (next: string) => void;
  label: string;
}) {
  const t = useT();
  if (!canEdit) {
    return (
      <span className="crm-mut crm-tnum text-[12.5px]">
        {value ? t("settings.support.hours", { h: value }) : "-"}
      </span>
    );
  }
  return (
    <span className="min-w-0">
      <label className="crm-inp">
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          inputMode="decimal"
          autoComplete="off"
          spellCheck={false}
          aria-label={label}
        />
      </label>
      {isBad(value) ? (
        <span className="mt-1 block text-[11.5px] text-destructive">{t("settings.support.badHours")}</span>
      ) : null}
    </span>
  );
}

export function SupportSettings({ user }: { user: StaffUser }) {
  const t = useT();
  const canEdit = userCanPlatform(user, "manage:crm.support");
  const [draft, setDraft] = useState<Draft>(() => toDraft(emptySlaTargets()));
  const [suggested, setSuggested] = useState<SlaTargets>(() => emptySlaTargets());
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  useEffect(() => {
    let alive = true;
    getSlaTargets()
      .then((res) => {
        if (!alive) return;
        setDraft(toDraft(res.targets));
        setSuggested(res.suggested);
      })
      .catch(() => undefined)
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const anyBad = SLA_PRIORITIES.some((p) => isBad(draft[p].first) || isBad(draft[p].resolve));

  const patch = (p: SlaPriority, key: "first" | "resolve", next: string) =>
    setDraft((prev) => ({ ...prev, [p]: { ...prev[p], [key]: next } }));

  // Fills the form, does NOT save. The operator still has to look at eight
  // numbers and press Save, which is the difference between a suggestion and a
  // default that arrived without anybody agreeing to it.
  const adopt = () => {
    setDraft(toDraft(suggested));
    setState("idle");
  };

  async function save() {
    if (anyBad) return;
    setState("saving");
    try {
      const res = await putSlaTargets(fromDraft(draft));
      setDraft(toDraft(res.targets));
      setState("saved");
      setTimeout(() => setState("idle"), 2500);
    } catch {
      setState("error");
    }
  }

  if (loading) {
    return (
      <Card>
        <div className="flex justify-center py-16 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      </Card>
    );
  }

  return (
    // `listCard` and NOT a plain Card: the scroll rule in index.css is
    // `.crm-card:has(> .crm-list-head)`, so the head has to be a DIRECT child.
    // An intermediate padding wrapper here left the card without its floor, and
    // the rows drew 82px past its own right edge - measured, not guessed.
    <Card style={listCard(COLS)}>
      <CardHead
        title={t("settings.support.sla.title")}
        sub={t("settings.support.sla.desc")}
        actions={
          canEdit ? (
            <>
              <Btn small onClick={adopt}>
                <Wand2 className="size-4" />
                {t("settings.support.useSuggested")}
              </Btn>
              <Btn small tone="dark" onClick={() => void save()} disabled={state === "saving" || anyBad}>
                {state === "saving" ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                {t("common.save")}
              </Btn>
            </>
          ) : null
        }
      />

      <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 18 }}>
        <span>{t("support.priority")}</span>
        <span>{t("support.sla.clock.firstResponse")}</span>
        <span>{t("support.sla.clock.resolve")}</span>
      </div>

      <div className="crm-rows pt-0">
        {SLA_PRIORITIES.map((p) => (
          <div key={p} className="crm-list-row" style={{ gridTemplateColumns: COLS }}>
            <span className="flex items-center text-[13px] font-semibold">
              {t(`support.priorityLabel.${p}`)}
            </span>
            <HourCell
              value={draft[p].first}
              placeholder={formatSlaHours(suggested.firstResponseMins[p])}
              canEdit={canEdit}
              onChange={(next) => patch(p, "first", next)}
              label={`${t(`support.priorityLabel.${p}`)} - ${t("support.sla.clock.firstResponse")}`}
            />
            <HourCell
              value={draft[p].resolve}
              placeholder={formatSlaHours(suggested.resolveMins[p])}
              canEdit={canEdit}
              onChange={(next) => patch(p, "resolve", next)}
              label={`${t(`support.priorityLabel.${p}`)} - ${t("support.sla.clock.resolve")}`}
            />
          </div>
        ))}
      </div>

      {/* The three things a reader cannot work out from the grid, and each of
          them changes how a red row should be read. */}
      <div className="flex flex-col gap-1.5 px-6 pb-4 pt-4">
        <p className="crm-sub">{t("settings.support.emptyHint")}</p>
        <p className="crm-sub">{t("settings.support.calendarHint")}</p>
        <p className="crm-sub">{t("settings.support.closedHint")}</p>
      </div>

      <div className="crm-tfoot">
        <span className="inline-flex items-center gap-1.5">
          <Timer className="size-3.5" />
          {canEdit ? t("settings.support.unit") : t("settings.support.readOnly")}
        </span>
        {state === "saved" ? <span className="crm-push text-[var(--color-success)]">{t("common.saved")}</span> : null}
        {state === "error" ? <span className="crm-push text-[var(--color-destructive)]">{t("common.error")}</span> : null}
      </div>
    </Card>
  );
}
